import { AllChip, CrewChip, ShiftChip } from "@/controls/chips";
import { getShiftsAndCrews } from "@/pages/delays/delays-report/repositories/DelaysReportRepository";
import { getMomentTz } from "@dexteel/mesf-core";
import {
	FormControl,
	Grid2 as Grid,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	SelectChangeEvent,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { get } from "lodash-es";
import { Moment } from "moment-timezone";
import React, { useEffect, useState } from "react";

type Props = {
	onFilterChange: (
		fromDate: Date,
		toDate: Date,
		shift: string | null,
		crew: string | null,
	) => void;
	initialFromDate: Date;
	initialToDate: Date;
	filterShift: string | null;
	onFilterShiftChange: (shift: string | null) => void;
	filterCrew: string | null;
	onFilterCrewChange: (crew: string | null) => void;
};

const moment = getMomentTz();

export const Filters: React.FC<Props> = ({
	onFilterChange,
	initialFromDate,
	initialToDate,
	filterShift,
	onFilterShiftChange,
	filterCrew,
	onFilterCrewChange,
}) => {
	const [fromDate, setFromDate] = useState<Date>(initialFromDate);
	const [toDate, setToDate] = useState<Date>(initialToDate);
	const [shiftsList, setShiftsList] = useState<any[]>([]);
	const [crewsList, setCrewsList] = useState<any[]>([]);

	useEffect(() => {
		const fetchOptions = async () => {
			const res = await getShiftsAndCrews();
			if (res.ok) {
				const rowsSource = get(res, "data", []);
				const shifts = rowsSource?.tables[0].rows;
				const crews = rowsSource?.tables[1].rows;
				setShiftsList(shifts);
				setCrewsList(crews);
			}
		};
		fetchOptions();
	}, []);

	const handleFromDateChange = (value: Moment | null) => {
		if (value) {
			const newDate = value.toDate();
			setFromDate(newDate);
			onFilterChange(newDate, toDate, filterShift, filterCrew);
		}
	};

	const handleToDateChange = (value: Moment | null) => {
		if (value) {
			const newDate = value.toDate();
			setToDate(newDate);
			onFilterChange(fromDate, newDate, filterShift, filterCrew);
		}
	};

	const handleShiftChange = (event: SelectChangeEvent<string>) => {
		const rawValue = event.target.value as string;
		const newShift = rawValue === "" ? null : rawValue;
		onFilterShiftChange(newShift);
		onFilterChange(fromDate, toDate, newShift, filterCrew);
	};

	const handleCrewChange = (event: SelectChangeEvent<string>) => {
		const rawValue = event.target.value as string;
		const newCrew = rawValue === "" ? null : rawValue;
		onFilterCrewChange(newCrew);
		onFilterChange(fromDate, toDate, filterShift, newCrew);
	};

	return (
		<Paper
			elevation={1}
			sx={{
				display: "inline-flex",
				alignItems: "center",
				p: 1,
			}}
		>
			<Grid container alignItems="center" spacing={1} wrap="nowrap">
				<Grid size="auto">
					<DatePicker
						label="From"
						format="MM/DD/YYYY"
						value={moment(fromDate)}
						onChange={handleFromDateChange}
						slotProps={{
							textField: {
								variant: "outlined",
								size: "small",
								sx: { width: 180 },
							},
						}}
					/>
				</Grid>
				<Grid size="auto">
					<DatePicker
						label="To"
						format="MM/DD/YYYY"
						value={moment(toDate)}
						onChange={handleToDateChange}
						disableFuture
						slotProps={{
							textField: {
								variant: "outlined",
								size: "small",
								sx: { width: 180 },
							},
						}}
					/>
				</Grid>
				<Grid size="auto">
					<FormControl variant="outlined" size="small" sx={{ minWidth: 90 }}>
						<InputLabel id="shift-select-label">Shift</InputLabel>
						<Select
							labelId="shift-select-label"
							id="shift-select"
							value={filterShift ?? ""}
							onChange={handleShiftChange}
							label="Shift"
						>
							<MenuItem key="shift-all" value="All">
								<div
									style={{ display: "flex", alignItems: "center", gap: "8px" }}
								>
									<AllChip size="small" />
									<span>All</span>
								</div>
							</MenuItem>
							{shiftsList.map((shift: any) => (
								<MenuItem key={`shift-${shift.Shift}`} value={shift.Shift}>
									<div
										style={{
											display: "flex",
											alignItems: "center",
											gap: "8px",
										}}
									>
										<ShiftChip shift={shift.Shift} size="small" />
										<span>{shift.Shift}</span>
									</div>
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
				<Grid size="auto">
					<FormControl variant="outlined" size="small" sx={{ minWidth: 90 }}>
						<InputLabel id="crew-select-label">Crew</InputLabel>
						<Select
							labelId="crew-select-label"
							id="crew-select"
							value={filterCrew ?? ""}
							onChange={handleCrewChange}
							label="Crew"
						>
							<MenuItem key="crew-all" value="All">
								<div
									style={{ display: "flex", alignItems: "center", gap: "8px" }}
								>
									<AllChip size="small" />
									<span>All</span>
								</div>
							</MenuItem>
							{crewsList.map((crew: any) => (
								<MenuItem key={`crew-${crew.Crew}`} value={crew.Crew}>
									<div
										style={{
											display: "flex",
											alignItems: "center",
											gap: "8px",
										}}
									>
										<CrewChip crew={crew.Crew} size="small" />
										<span>{crew.Crew}</span>
									</div>
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
			</Grid>
		</Paper>
	);
};
